/**
 * The per-entry read table on the KB Usage tab.
 *
 * Pure input->HTML: the sort state and the data are passed in, so nothing here
 * fetches or remembers anything. Split out of kb_usage.ts when that file grew a
 * second heatmap - the table and the grids are two different answers to two
 * different questions, and only one of them changed.
 */

import { esc } from '../../shared/rendering.js'
import { action } from '../components/action-delegation.js'
import type { KBReadStatsWindow } from '../../api/kb.js'

export type UsageSort = { col: keyof KBReadStatsWindow; dir: 'asc' | 'desc' }

/** Sort a copy, never the caller's array: the source order is state elsewhere. */
export function sortRows(rows: readonly KBReadStatsWindow[], sort: UsageSort): KBReadStatsWindow[] {
  const dir = sort.dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const av = a[sort.col]
    const bv = b[sort.col]
    if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * dir
    return ((av as number) - (bv as number)) * dir
  })
}

function renderSortHeader(sort: UsageSort, col: keyof KBReadStatsWindow, label: string): string {
  const active = sort.col === col
  const arrow = active ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''
  const nextDir = active && sort.dir === 'desc' ? 'asc' : 'desc'
  return `<th style="cursor:pointer;text-align:right;padding:6px 10px;font-size:11px;color:var(--${active ? 'cyn' : 't3'})" ${action('setKbUsageSort', `${col}:${nextDir}`)}>${label}${arrow}</th>`
}

/**
 * Header line with the rolling totals, plus the table itself.
 *
 * `failed` exists because "no rows" and "the request did not come back" are
 * different facts and were being reported with the same sentence. Saying
 * "No per-entry read activity recorded yet" when a fetch has failed is a claim
 * about the knowledge base that we are in no position to make.
 */
export function renderUsageTable(
  sorted: readonly KBReadStatsWindow[],
  sort: UsageSort,
  failed = false,
): string {
  // Totals are only rendered when there is something to total.
  //
  // Built unconditionally, they printed "0 entries with reads · 1d: 0 · 7d: 0"
  // directly above "Could not load per-entry read activity" -- reduce() over an
  // empty array, presented as fact, one line above the admission that we have
  // no data. Dashes say "unknown"; zero says "none", and only one of those is
  // true.
  const figure = (n: number): string => (failed ? '—' : String(n))
  const count = failed
    ? 'Read activity per entry'
    : `Read activity per entry · ${String(sorted.length)} entries with reads`

  const header = `<header style="display:flex;align-items:baseline;justify-content:space-between">
    <div><p class="page-subtitle">${count}</p></div>
    <div style="display:flex;gap:12px;font-size:10px;color:var(--t3);font-family:var(--mono)">
      <span>1d: ${figure(sorted.reduce((s, r) => s + r.last1d, 0))}</span><span>7d: ${figure(sorted.reduce((s, r) => s + r.last7d, 0))}</span><span>30d: ${figure(sorted.reduce((s, r) => s + r.last30d, 0))}</span>
    </div>
  </header>`

  if (failed) {
    return `${header}<div class="empty">Could not load per-entry read activity.</div>`
  }
  if (sorted.length === 0) {
    return `${header}<div class="empty">No per-entry read activity recorded yet.</div>`
  }

  const rows = sorted
    .map(
      (r) =>
        `<tr>
      <td style="text-align:left;padding:6px 10px;max-width:300px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">
        <a href="#/kb/${encodeURIComponent(r.entryId)}" style="color:var(--cyn);text-decoration:none;font-size:12px" title="${esc(r.title)}">${esc(r.title)}</a>
        <span style="display:block;font-size:9px;color:var(--t4);font-family:var(--mono)">${esc(r.type)}</span>
      </td>
      <td style="text-align:right;padding:6px 10px;font-family:var(--mono);font-size:11px;color:var(--t2)">${r.last1d || '—'}</td>
      <td style="text-align:right;padding:6px 10px;font-family:var(--mono);font-size:11px;color:var(--${r.last7d > 0 ? 'grn' : 't3'})">${r.last7d || '—'}</td>
      <td style="text-align:right;padding:6px 10px;font-family:var(--mono);font-size:11px;color:var(--t3)">${r.last30d || '—'}</td>
      <td style="text-align:right;padding:6px 10px;font-family:var(--mono);font-size:10px;color:var(--t4)">${r.total || '—'}</td>
    </tr>`,
    )
    .join('')

  return `${header}
  <div style="overflow-x:auto;margin-top:12px">
    <table style="width:100%;border-collapse:collapse">
      <thead>
        <tr>
          <th style="text-align:left;padding:6px 10px;font-size:11px;color:var(--t3)">Entry</th>
          ${renderSortHeader(sort, 'last1d', '1d')}
          ${renderSortHeader(sort, 'last7d', '7d')}
          ${renderSortHeader(sort, 'last30d', '30d')}
          ${renderSortHeader(sort, 'total', 'Total')}
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  </div>`
}
